import { groupBy } from 'lodash';
import { sumByKey } from 'utils/collectionUtils';
import { getSafeNumber } from 'utils/numberUtils';
import useGetProgressBudget, { TUseProgressBudgetData } from './useGetProgressBudget';
import useGetProgressBudgetActuals, { TUseProgressBudgetActualsData } from './useGetProgressBudgetActuals';

export interface IProgressBudgetActuals {
  budget: number;
  actual: number;
  quantityBudget: number;
  quantityActual: number;
}

export default function useGetProgressBudgetActualsData() {
  const getAllProgressBudgetData = useGetProgressBudget();
  const getProgressActualsData = useGetProgressBudgetActuals();

  async function getProgressBudgetActualsData(
    projectIds: string[],
    costCodeIds: string[],
    includeSubprojects: boolean
  ): Promise<Map<string, IProgressBudgetActuals>> {
    if (!projectIds || projectIds.length === 0) {
      return new Map();
    }

    const progressBudgetsPromise = getAllProgressBudgetData(projectIds, costCodeIds, includeSubprojects, null);
    const progressActualsPromise = getProgressActualsData(projectIds, costCodeIds, includeSubprojects, null);
    const [progressBudgets, progressActuals] = await Promise.all([progressBudgetsPromise, progressActualsPromise]);

    const budgetsByProject = groupBy(progressBudgets, 'projectId');
    const actualsByProject = groupBy(progressActuals, 'projectId');

    const resultMap = new Map<string, IProgressBudgetActuals>();

    for (const projectId of projectIds) {
      const pBudgets = budgetsByProject[projectId] || [];
      const pActuals = actualsByProject[projectId] || [];
      resultMap.set(
        projectId,
        aggregateProgressBudgetsToActuals(
          pBudgets as TUseProgressBudgetData[],
          pActuals as TUseProgressBudgetActualsData[]
        )
      );
    }

    return resultMap;
  }

  return getProgressBudgetActualsData;
}

export function aggregateProgressBudgetsToActuals(
  budgets: TUseProgressBudgetData[],
  progressActuals: TUseProgressBudgetActualsData[]
): IProgressBudgetActuals {
  let budgeted = 0;
  let actual = 0;
  let budgetedQuantity = 0;

  const quantityActual = sumByKey(progressActuals, 'amount') ?? 0;
  const groupedByProjectIdCostCodeId = groupBy(
    progressActuals,
    ({ projectId, costCodeId }) => `${projectId}:${costCodeId}`
  );

  budgets.forEach((budget) => {
    const actuals = groupedByProjectIdCostCodeId[`${budget.projectId}:${budget.costCodeId}`];
    const quantity = sumByKey(actuals, 'amount');

    const budgetValue = getSafeNumber(budget.value);
    const budgetQuantity = getSafeNumber(budget.quantity);

    budgeted += budgetValue;

    const progressActual = getProgressActual({ budgetValue, budgetQuantity, quantity });
    actual += progressActual;

    budgetedQuantity += budgetQuantity;
  });

  return {
    budget: budgeted,
    actual,
    quantityBudget: budgetedQuantity,
    quantityActual,
  } as IProgressBudgetActuals;
}

export function getProgressActual({
  budgetValue,
  budgetQuantity,
  quantity,
}: {
  budgetValue: number;
  budgetQuantity: number;
  quantity: number;
}) {
  const progressRate = budgetQuantity == 0.0 ? 0.0 : budgetValue / budgetQuantity;
  return quantity * progressRate;
}
